// Agent Status Banner - Banner de estado del agente (refactorizado para auto-conexion)
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAgentStore } from '../stores/agentStore';
import { isAgentOutdated, LATEST_ENGINE_VERSION } from '../lib/agentVersions';
import { AlertCircle, AlertTriangle, CheckCircle, Clock3, Download, ShieldAlert, Wrench, Loader2 } from 'lucide-react';
import { Button } from './ui/button';

type StatusConfig = {
  containerClass: string;
  titleClass: string;
  detailClass: string;
  icon: typeof CheckCircle;
  title: string;
  detail: string;
  showSpinner: boolean;
  spinnerText: string;
  showActions: boolean;
};

export function AgentStatusBanner() {
  const navigate = useNavigate();
  const { status, checkStatus, startAutoCheck, stopAutoCheck, wasEverConnected } = useAgentStore();

  useEffect(() => {
    startAutoCheck();
    return () => stopAutoCheck();
  }, [startAutoCheck, stopAutoCheck]);

  const getStatusConfig = (): StatusConfig => {
    // disconnected se bifurca segun wasEverConnected
    if (status.status === 'disconnected') {
      if (!wasEverConnected) {
        return {
          containerClass: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800',
          titleClass: 'text-amber-900 dark:text-amber-300',
          detailClass: 'text-amber-700 dark:text-amber-400',
          icon: AlertTriangle,
          title: 'Agente no detectado',
          detail: 'Abre ProcesoFacil desde el acceso directo en tu escritorio.',
          showSpinner: true,
          spinnerText: 'Buscando agente...',
          showActions: false,
        };
      }
      return {
        containerClass: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800',
        titleClass: 'text-red-900 dark:text-red-300',
        detailClass: 'text-red-700 dark:text-red-400',
        icon: AlertCircle,
        title: 'Se perdio la conexion con el agente',
        detail: status.lastSeen
          ? `Verifica que ProcesoFacil siga abierto. Ultima conexion: ${status.lastSeen}`
          : 'Verifica que ProcesoFacil siga abierto en tu equipo.',
        showSpinner: true,
        spinnerText: 'Reconectando...',
        showActions: true,
      };
    }

    // unauthorized: agente legacy que requiere API Key
    if (status.status === 'unauthorized') {
      return {
        containerClass: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800',
        titleClass: 'text-amber-900 dark:text-amber-300',
        detailClass: 'text-amber-700 dark:text-amber-400',
        icon: ShieldAlert,
        title: 'Hay una version anterior del agente',
        detail: 'Actualiza ProcesoFacil para una mejor experiencia, o configura la API Key en Configuracion avanzada.',
        showSpinner: false,
        spinnerText: '',
        showActions: true,
      };
    }

    // Agente conectado pero con version antigua del motor
    if (status.status === 'connected' && isAgentOutdated(status.engine_version)) {
      const currentVer = status.engine_version || '?';
      return {
        containerClass: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800',
        titleClass: 'text-blue-900 dark:text-blue-300',
        detailClass: 'text-blue-700 dark:text-blue-400',
        icon: Download,
        title: 'Actualizacion disponible para el agente',
        detail: `Tu agente (v${currentVer}) necesita actualizarse a v${LATEST_ENGINE_VERSION}. Descarga la nueva version para acceder a todas las funciones.`,
        showSpinner: false,
        spinnerText: '',
        showActions: true,
      };
    }

    const configs: Record<string, StatusConfig> = {
      connected: {
        containerClass: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800',
        titleClass: 'text-green-900 dark:text-green-300',
        detailClass: 'text-green-700 dark:text-green-400',
        icon: CheckCircle,
        title: 'Agente conectado y listo',
        detail: '',
        showSpinner: false,
        spinnerText: '',
        showActions: false,
      },
      degraded: {
        containerClass: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800',
        titleClass: 'text-amber-900 dark:text-amber-300',
        detailClass: 'text-amber-700 dark:text-amber-400',
        icon: AlertTriangle,
        title: 'Agente disponible con capacidades limitadas',
        detail: status.degraded_reason || 'Algunos motores no estan disponibles, pero el agente sigue respondiendo.',
        showSpinner: false,
        spinnerText: '',
        showActions: true,
      },
      timeout: {
        containerClass: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800',
        titleClass: 'text-amber-900 dark:text-amber-300',
        detailClass: 'text-amber-700 dark:text-amber-400',
        icon: Clock3,
        title: 'El agente tardo demasiado en responder',
        detail: status.error || 'Reintenta en unos segundos o revisa la carga del agente.',
        showSpinner: false,
        spinnerText: '',
        showActions: true,
      },
      error: {
        containerClass: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800',
        titleClass: 'text-red-900 dark:text-red-300',
        detailClass: 'text-red-700 dark:text-red-400',
        icon: AlertCircle,
        title: 'El agente respondio con un error interno',
        detail: status.error || 'El agente esta activo, pero no puede operar correctamente.',
        showSpinner: false,
        spinnerText: '',
        showActions: true,
      },
    };

    return configs[status.status] || configs.error;
  };

  const currentStatus = getStatusConfig();
  const StatusIcon = currentStatus.icon;

  // Mostrar banner de actualizacion si esta conectado pero con version antigua
  const agentOutdated = status.status === 'connected' && isAgentOutdated(status.engine_version);

  // No mostrar banner cuando esta conectado y actualizado
  if (status.status === 'connected' && !agentOutdated) {
    return null;
  }

  return (
    <div
      role="alert"
      className={`
        px-4 py-3 border-b flex items-center justify-between
        ${currentStatus.containerClass}
      `}
    >
      <div className="flex items-center gap-3">
        <StatusIcon className={`w-5 h-5 flex-shrink-0 ${currentStatus.titleClass}`} />

        <div>
          <div>
            <span className={`font-medium ${currentStatus.titleClass}`}>
              {currentStatus.title}
            </span>
            {currentStatus.detail && (
              <span className={`text-sm ml-2 ${currentStatus.detailClass}`}>
                {currentStatus.detail}
              </span>
            )}
          </div>
          {currentStatus.showSpinner && (
            <div className={`flex items-center gap-1.5 mt-1 text-xs ${currentStatus.detailClass}`}>
              <Loader2 className="w-3 h-3 animate-spin" />
              {currentStatus.spinnerText}
            </div>
          )}
        </div>
      </div>

      {currentStatus.showActions && (
        <div className="flex flex-col sm:flex-row gap-2">
          {(status.status === 'unauthorized' || agentOutdated) && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/download')}
              className="w-full sm:w-auto"
            >
              <Download className="w-4 h-4" />
              Descargar actualizacion
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/settings', { state: { openAdvanced: true } })}
            className="w-full sm:w-auto"
          >
            <Wrench className="w-4 h-4" />
            Configuracion
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={checkStatus}
            className="w-full sm:w-auto"
          >
            ↻ Reintentar
          </Button>
        </div>
      )}
    </div>
  );
}
